import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { Effect, Match, Schema } from "effect";
import { decodeUnknownOption } from "../../../packages/common/src/schema.ts";
import { ApiError, Credential } from "#api.ts";
import { useAuth } from "#auth.tsx";
import { runEffectQuery } from "#effect-query.ts";
import { OrderEvents, type ConnectionState } from "#order-events.ts";
import {
  formatMoney,
  Order,
  orderSchema,
  singleOrderSchema,
  statusLabels,
} from "#orders.ts";
import { OrderLines } from "#order-dialog.tsx";

const eventSchema = Schema.Struct({
  type: Schema.Literal("order.updated"),
  payload: orderSchema,
});

/** Public single-order view using an expiring, order-scoped credential. */
export function OrderTracking() {
  const { orderId } = useParams();
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const liveSequence = useRef(0);

  const [credential] = useState(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const query = new URLSearchParams(window.location.search);
    const token = hash.get("token") ?? query.get("token");

    return token ? new Credential(token) : undefined;
  });

  const [connection, setConnection] = useState<ConnectionState>("connecting");
  // Remove legacy query tokens from the address before navigation or telemetry.
  useEffect(() => {
    if (window.location.search)
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${credential ? `#token=${encodeURIComponent(credential.reveal())}` : ""}`,
      );
  }, [credential]);

  const orderQueryKey = useMemo(
    () => ["orders", "tracking", orderId] as const,
    [orderId],
  );
  const tracking = useQuery<Order, ApiError>({
    queryKey: orderQueryKey,
    enabled: Boolean(orderId),
    retry: false,
    queryFn: ({ signal }) => {
      const input = {
        path: `/orders/${encodeURIComponent(orderId ?? "")}`,
        schema: singleOrderSchema,
        signal,
      };
      const request = credential
        ? api.publicRequestEffect(input, credential)
        : api.requestEffect(input);

      return runEffectQuery(
        request.pipe(
          Effect.flatMap((response) => {
            const found = response.data.find(
              (item) => item.squareId === orderId,
            );

            return found
              ? Effect.succeed(found)
              : Effect.fail(new ApiError(404, "Order not found."));
          }),
        ),
        signal,
      );
    },
  });
  useEffect(() => {
    if (!orderId) return;

    const events = new OrderEvents({
      config: api.config,
      channel: `/default/${orderId}`,
      credential: async () =>
        credential
          ? { ok: true, value: credential }
          : api.snapshot().kind === "authenticated"
            ? api.credential()
            : {
                ok: false,
                error: new ApiError(401, "Sign in or scan a tracking QR."),
              },
      state: setConnection,
      resynchronise: () => {
        void queryClient.invalidateQueries({ queryKey: orderQueryKey });
      },
      event: (value) => {
        const parsed = decodeUnknownOption(eventSchema, value);

        // An event this page cannot read still means the order changed:
        // refetch instead of silently leaving a stale status on screen.
        if (!parsed) {
          void queryClient.invalidateQueries({ queryKey: orderQueryKey });
          return;
        }

        if (parsed.payload.squareId === orderId) {
          const sequence = ++liveSequence.current;

          void queryClient.cancelQueries({ queryKey: orderQueryKey }).then(() => {
            if (sequence === liveSequence.current)
              queryClient.setQueryData(orderQueryKey, parsed.payload);
          });
        }
      },
    });

    events.start();

    const visible = () => {
      if (document.visibilityState === "visible") {
        events.stop();
        events.start();
        void queryClient.invalidateQueries({ queryKey: orderQueryKey });
      }
    };

    document.addEventListener("visibilitychange", visible);

    return () => {
      events.stop();
      document.removeEventListener("visibilitychange", visible);
    };
  }, [api, orderId, credential, queryClient, orderQueryKey]);

  const result = Match.value(tracking).pipe(
    Match.when({ status: "pending" }, () => (
      <>
        <h1>Order tracking</h1>
        <p role="status">Loading your order…</p>
      </>
    )),
    Match.when({ status: "error" }, ({ error }) =>
      Match.valueTags(error, {
        ApiError: (failure) => (
          <>
            <h1>Order tracking</h1>
            <p role="alert" className="alert">
              {failure.status === 401 || failure.status === 403
                ? "This tracking link has expired or is not valid for this order. Ask staff for a new QR code."
                : failure.message}
            </p>
          </>
        ),
      }),
    ),
    Match.when({ status: "success" }, ({ data: order }) => (
      <>
        <h1>{order.label}</h1>
        <section className="settings-card">
          <span className={`pill ${order.state}`}>
            {statusLabels[order.state]}
          </span>
          <h2>
            {order.state === "order_ready"
              ? "Your onigiri is ready!"
              : order.state === "order_completed"
                ? "Enjoy your onigiri!"
                : order.state === "order_canceled"
                  ? "This order was cancelled"
                  : order.state === "order_refunded"
                    ? "This order was refunded"
                    : "We’re preparing your order"}
          </h2>
          <p>
            {order.state === "order_ready"
              ? "Please come and collect your order."
              : "You can keep this page open for updates."}
          </p>
          <p className="muted">
            {connection === "live" ? "Live updates connected" : "Reconnecting…"}
          </p>
          <details className="tracking-details">
            <summary>See order details</summary>
            <OrderLines order={order} />
            <div className="order-total">
              <strong>Total</strong>
              <strong>{formatMoney(order.total)}</strong>
            </div>
          </details>
        </section>
      </>
    )),
    Match.exhaustive,
  );

  return (
    <main className="tracking-page">
      <Link to="/login">
        <img
          className="brand-logo large"
          src="/title-icon.png"
          alt="Pocket Rice"
        />
      </Link>
      <p className="eyebrow">Your order</p>
      {result}
      <button
        className="secondary"
        disabled={tracking.isFetching}
        onClick={() => void tracking.refetch()}
      >
        Refresh order
      </button>
      <p>
        <a
          href="https://www.instagram.com/pocket_rice_byronbay/"
          rel="noreferrer"
        >
          Follow Pocket Rice on Instagram
        </a>
      </p>
    </main>
  );
}
